import React, { useState, useEffect } from 'react';
import { Settings, Save, MessageCircle, DollarSign, Mail, Building2, Cloud, RefreshCw, Loader2, CheckCircle, XCircle, Play, FileText, KeyRound, Download, Filter, Bell, Send, AlertTriangle, Clock } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAdminSettings, updateSettings, testSharePointConnection, triggerSharePointSync, getSharePointSyncLog, adminChangePassword, exportCustomerPriceList, getAlertSettings, updateAlertSettings, sendTestAlert, getAlertLog } from '../../lib/api';
import { Toaster, toast } from 'sonner';

export const AdminSettings = () => {
  const { t } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncLog, setSyncLog] = useState([]);
  const [showSyncLog, setShowSyncLog] = useState(false);
  const [settings, setSettings] = useState({
    whatsapp_number: '',
    admin_email: '',
    bank_name: '',
    beneficiary_name: '',
    account_iban: '',
    swift_code: '',
    bank_address: '',
    wire_instructions_template: '',
    proforma_template: '',
    sharepoint: {
      enabled: false,
      tenant_id: '',
      client_id: '',
      client_secret: '',
      site_url: '',
      drive_id: '',
      folder_path: '/',
      sync_interval_minutes: 60,
    },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getAdminSettings();
        const d = res.data;
        setSettings({
          whatsapp_number: d.whatsapp_number || '',
          admin_email: d.admin_email || '',
          bank_name: d.bank_name || '',
          beneficiary_name: d.beneficiary_name || '',
          account_iban: d.account_iban || '',
          swift_code: d.swift_code || '',
          bank_address: d.bank_address || '',
          wire_instructions_template: d.wire_instructions_template || '',
          proforma_template: d.proforma_template || '',
          sharepoint: {
            enabled: d.sharepoint?.enabled || false,
            tenant_id: d.sharepoint?.tenant_id || '',
            client_id: d.sharepoint?.client_id || '',
            client_secret: d.sharepoint?.client_secret || '',
            site_url: d.sharepoint?.site_url || '',
            drive_id: d.sharepoint?.drive_id || '',
            folder_path: d.sharepoint?.folder_path || '/',
            sync_interval_minutes: d.sharepoint?.sync_interval_minutes || 60,
          },
        });
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success(t('adminSettings.saved'));
    } catch (err) {
      toast.error(t('adminSettings.failedSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const res = await testSharePointConnection();
      setConnectionResult(res.data);
    } catch (err) {
      setConnectionResult({ success: false, message: err.response?.data?.detail || t('adminSettings.connectionFailed') });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await triggerSharePointSync();
      toast.success(t('adminSettings.syncStarted'));
    } catch (err) {
      toast.error(t('adminSettings.failedTriggerSync'));
    } finally {
      setSyncing(false);
    }
  };

  const handleViewLog = async () => {
    try {
      const res = await getSharePointSyncLog();
      setSyncLog(res.data);
      setShowSyncLog(true);
    } catch (err) {
      toast.error(t('adminSettings.failedLoadSyncLog'));
    }
  };

  const updateSP = (field, value) => {
    setSettings(prev => ({
      ...prev,
      sharepoint: { ...prev.sharepoint, [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="card-dark p-8 text-center">
          <div className="animate-pulse text-white/40">{t('adminSettings.loadingSettings')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-settings">
      <Toaster position="top-right" theme="dark" />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Settings className="w-8 h-8 text-[#00D4FF]" />
            {t('adminSettings.title')}
          </h1>
          <p className="text-white/60 mt-1">{t('adminSettings.configurePortal')}</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-settings-btn">
          <Save className="w-4 h-4" />
          {saving ? t('adminSettings.saving') : t('adminSettings.saveChanges')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Contact Settings */}
        <div className="card-dark p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#00D4FF]" />
            {t('adminSettings.contactSettings')}
          </h2>
          <div className="space-y-6">
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.whatsappNumber')}</label>
              <input type="tel" value={settings.whatsapp_number} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                placeholder="+1234567890" className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]" data-testid="whatsapp-input" />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.adminEmailLabel')}</label>
              <input type="email" value={settings.admin_email} onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
                placeholder="sales@luxortek.com" className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]" data-testid="admin-email-input" />
            </div>
          </div>
        </div>

        {/* Wire Transfer Settings */}
        <div className="card-dark p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#00D4FF]" />
            {t('adminSettings.wireTransferDetails')}
          </h2>
          <div className="space-y-4">
            {[
              { key: 'bank_name', label: t('adminSettings.bankName'), placeholder: 'ABC International Bank', testid: 'bank-name-input' },
              { key: 'beneficiary_name', label: t('adminSettings.beneficiaryName'), placeholder: 'LUXOR TEK LLC', testid: 'beneficiary-input' },
              { key: 'account_iban', label: t('adminSettings.accountIban'), placeholder: 'XXXXXXXX', testid: 'account-iban-input', mono: true },
              { key: 'swift_code', label: t('adminSettings.swiftBic'), placeholder: 'ABCDEFGH', testid: 'swift-code-input', mono: true },
              { key: 'bank_address', label: t('adminSettings.bankAddress'), placeholder: '123 Bank Street, Miami, FL', testid: 'bank-address-input' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-white/60 text-sm mb-2">{f.label}</label>
                <input type="text" value={settings[f.key]} onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                  placeholder={f.placeholder} className={`w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] ${f.mono ? 'font-mono' : ''}`} data-testid={f.testid} />
              </div>
            ))}
          </div>
        </div>

        {/* Wire Instructions */}
        <div className="card-dark p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#00D4FF]" />
            {t('adminSettings.additionalWireInstructions')}
          </h2>
          <textarea value={settings.wire_instructions_template} onChange={(e) => setSettings({ ...settings, wire_instructions_template: e.target.value })}
            rows={5} placeholder={t('adminSettings.wireInstructionsPlaceholder')} className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] resize-none font-mono text-sm" data-testid="wire-instructions-input" />
        </div>

        {/* Proforma Notes */}
        <div className="card-dark p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#00D4FF]" />
            {t('adminSettings.proformaNotes')}
          </h2>
          <textarea value={settings.proforma_template} onChange={(e) => setSettings({ ...settings, proforma_template: e.target.value })}
            rows={5} placeholder={t('adminSettings.proformaNotesPlaceholder')} className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] resize-none font-mono text-sm" data-testid="proforma-template-input" />
        </div>
      </div>

      {/* SharePoint Integration */}
      <div className="mt-8">
        <div className="card-dark p-6" data-testid="sharepoint-settings">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Cloud className="w-5 h-5 text-[#00D4FF]" />
              {t('adminSettings.sharepointAutoSync')}
            </h2>
            <label className="flex items-center gap-3 cursor-pointer">
              <span className="text-sm text-white/60">{settings.sharepoint.enabled ? t('adminSettings.enabled') : t('adminSettings.disabled')}</span>
              <div className="relative">
                <input type="checkbox" checked={settings.sharepoint.enabled} onChange={(e) => updateSP('enabled', e.target.checked)} className="sr-only" data-testid="sp-enabled-toggle" />
                <div className={`w-11 h-6 rounded-full transition-colors ${settings.sharepoint.enabled ? 'bg-[#00D4FF]' : 'bg-white/20'}`}>
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.sharepoint.enabled ? 'translate-x-5' : ''}`}></div>
                </div>
              </div>
            </label>
          </div>
          <p className="text-white/50 text-sm mb-6">{t('adminSettings.sharepointAutoSyncDesc')}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.tenantId')}</label>
              <input type="text" value={settings.sharepoint.tenant_id} onChange={(e) => updateSP('tenant_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] font-mono text-sm" data-testid="sp-tenant-id" />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.clientIdApp')}</label>
              <input type="text" value={settings.sharepoint.client_id} onChange={(e) => updateSP('client_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] font-mono text-sm" data-testid="sp-client-id" />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.clientSecret')}</label>
              <input type="password" value={settings.sharepoint.client_secret} onChange={(e) => updateSP('client_secret', e.target.value)}
                placeholder={t('adminSettings.enterClientSecret')} className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] font-mono text-sm" data-testid="sp-client-secret" />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.siteUrl')}</label>
              <input type="text" value={settings.sharepoint.site_url} onChange={(e) => updateSP('site_url', e.target.value)}
                placeholder="contoso.sharepoint.com:/sites/MySite" className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] text-sm" data-testid="sp-site-url" />
              <p className="text-white/30 text-xs mt-1">{t('adminSettings.siteUrlFormat')}</p>
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.driveId')}</label>
              <input type="text" value={settings.sharepoint.drive_id} onChange={(e) => updateSP('drive_id', e.target.value)}
                placeholder={t('adminSettings.driveIdPlaceholder')} className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] font-mono text-sm" data-testid="sp-drive-id" />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.folderPath')}</label>
              <input type="text" value={settings.sharepoint.folder_path} onChange={(e) => updateSP('folder_path', e.target.value)}
                placeholder="/Inventory" className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] text-sm" data-testid="sp-folder-path" />
              <p className="text-white/30 text-xs mt-1">{t('adminSettings.folderPathHint')}</p>
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('adminSettings.syncInterval')}</label>
              <select value={settings.sharepoint.sync_interval_minutes} onChange={(e) => updateSP('sync_interval_minutes', parseInt(e.target.value))}
                className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF] text-sm" data-testid="sp-interval">
                <option value={15}>{t('adminSettings.every15min')}</option>
                <option value={30}>{t('adminSettings.every30min')}</option>
                <option value={60}>{t('adminSettings.everyHour')}</option>
                <option value={240}>{t('adminSettings.every4hours')}</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-4">
            <button onClick={handleTestConnection} disabled={testingConnection} className="btn-secondary flex items-center gap-2 text-sm" data-testid="sp-test-btn">
              {testingConnection ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {testingConnection ? t('adminSettings.testing') : t('adminSettings.testConnection')}
            </button>
            <button onClick={handleSyncNow} disabled={syncing || !settings.sharepoint.enabled} className="btn-secondary flex items-center gap-2 text-sm" data-testid="sp-sync-now-btn">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {t('adminSettings.syncNow')}
            </button>
            <button onClick={handleViewLog} className="btn-secondary flex items-center gap-2 text-sm" data-testid="sp-view-log-btn">
              <FileText className="w-4 h-4" /> {t('adminSettings.viewSyncLog')}
            </button>
          </div>

          {/* Connection Result */}
          {connectionResult && (
            <div className={`p-4 border text-sm ${connectionResult.success ? 'bg-[#10B981]/10 border-[#10B981]/30' : 'bg-red-500/10 border-red-500/30'}`} data-testid="sp-connection-result">
              <div className="flex items-center gap-2 mb-1 font-medium">
                {connectionResult.success ? <CheckCircle className="w-4 h-4 text-[#10B981]" /> : <XCircle className="w-4 h-4 text-red-400" />}
                {connectionResult.success ? t('adminSettings.connectionSuccessful') : t('adminSettings.connectionFailedMsg')}
              </div>
              <p className="text-white/60">{connectionResult.message}</p>
              {connectionResult.files?.length > 0 && (
                <div className="mt-2">
                  <p className="text-white/40 text-xs mb-1">{t('adminSettings.filesFound')}</p>
                  <div className="flex flex-wrap gap-1">
                    {connectionResult.files.map((f, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-white/5 font-mono">{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sync Log */}
          {showSyncLog && (
            <div className="mt-4" data-testid="sp-sync-log">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-white/70">{t('adminSettings.syncLogTitle')}</h3>
                <button onClick={() => setShowSyncLog(false)} className="text-white/40 hover:text-white text-xs">{t('adminSettings.closeSyncLog')}</button>
              </div>
              {syncLog.length === 0 ? (
                <p className="text-white/40 text-sm">{t('adminSettings.noSyncLogs')}</p>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {syncLog.map((log, i) => (
                    <div key={i} className={`px-3 py-2 text-xs font-mono border-l-2 ${
                      log.level === 'error' ? 'border-red-500 bg-red-500/5' :
                      log.level === 'skipped' ? 'border-[#F59E0B] bg-[#F59E0B]/5' :
                      log.level === 'success' ? 'border-[#10B981] bg-[#10B981]/5' :
                      'border-white/20 bg-white/5'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="text-white/80">{log.message}</span>
                        <span className="text-white/30">{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                      {log.details && <p className="text-white/40 mt-0.5">{log.details}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Alerts & Notifications */}
      <AlertSettingsSection />

      {/* Customer Price List Export */}
      <CustomerPriceListExport t={t} />

      {/* How to Use */}
      <div className="mt-4 p-6 bg-[#10B981]/5 border border-[#10B981]/20">
        <h3 className="font-semibold text-[#10B981] mb-2">{t('adminSettings.howWireTransferWorks')}</h3>
        <ol className="text-white/60 text-sm space-y-2 list-decimal list-inside">
          <li>{t('adminSettings.wireStep1')}</li>
          <li>{t('adminSettings.wireStep2')}</li>
          <li>{t('adminSettings.wireStep3')}</li>
          <li>{t('adminSettings.wireStep4')}</li>
          <li>{t('adminSettings.wireStep5')}</li>
        </ol>
      </div>

      {/* Change Password */}
      <AdminChangePasswordSection t={t} />
    </div>
  );
};

const ALERT_LABELS = {
  new_registration: { label: 'New Customer Registration', icon: '1' },
  contact_form: { label: 'New Contact Form Submission', icon: '2' },
  price_list_request: { label: 'Price List Request', icon: '3' },
  quote_request: { label: 'New Quote / Order Request', icon: '4' },
  proforma_failure: { label: 'Failed Proforma Generation', icon: '5' },
  email_failure: { label: 'Failed Customer Email Send', icon: '6' },
};

const AlertSettingsSection = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // Fetch settings from the main endpoint (cache-busted)
  const fetchSettings = async () => {
    try {
      const res = await getAlertSettings();
      console.log('[LUXOR_ALERTS] whatsapp_configured =', res.data?.whatsapp_configured);
      if (res.data && typeof res.data === 'object') {
        setSettings(res.data);
      }
    } catch {
      toast.error('Failed to load alert settings');
    }
  };

  useEffect(() => {
    fetchSettings().finally(() => setLoading(false));
  }, []);

  const save = async (updates) => {
    setSaving(true);
    try {
      await updateAlertSettings(updates);
      // Always re-fetch from GET (cache-busted) instead of relying on PUT response
      await fetchSettings();
      toast.success('Alert settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleAlert = (type) => {
    const next = { ...settings.alerts, [type]: !settings.alerts[type] };
    save({ alerts: next });
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await sendTestAlert();
      if (res.data.success) {
        toast.success('Test alert sent! Check your WhatsApp.');
      } else {
        toast.error(res.data.error || 'Test failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test alert failed');
    } finally { setTesting(false); }
  };

  const loadLogs = async () => {
    setShowLog(!showLog);
    if (!showLog) {
      setLogLoading(true);
      try {
        const res = await getAlertLog(30);
        setLogs(res.data);
      } catch { toast.error('Failed to load logs'); }
      finally { setLogLoading(false); }
    }
  };

  if (loading) return <div className="mt-8 card-dark p-6"><Loader2 className="w-5 h-5 animate-spin text-[#00D4FF]" /></div>;
  if (!settings) return null;

  // Derive connected status directly from settings response
  const isConnected = settings?.whatsapp_configured === true;

  return (
    <div className="mt-8" data-testid="alert-settings-section">
      <div className="card-dark p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#00D4FF]" />
              Alerts & Notifications
            </h2>
            <p className="text-white/50 text-sm mt-1">
              Receive instant WhatsApp alerts for high-priority business events.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <span className="flex items-center gap-1 text-xs text-[#10B981] bg-[#10B981]/10 px-2 py-1" data-testid="wa-connected-badge"><CheckCircle className="w-3 h-3" /> WhatsApp API Connected</span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-[#F59E0B] bg-[#F59E0B]/10 px-2 py-1" data-testid="wa-not-configured-badge"><AlertTriangle className="w-3 h-3" /> WhatsApp Not Configured</span>
            )}
          </div>
        </div>

        {/* Channel toggles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-[#050505] border border-white/10 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#25D366]" />
                <span className="font-medium text-sm">WhatsApp Alerts</span>
              </div>
              <button
                onClick={() => save({ whatsapp_enabled: !settings.whatsapp_enabled })}
                className={`w-10 h-5 rounded-full transition-colors relative ${settings.whatsapp_enabled ? 'bg-[#25D366]' : 'bg-white/20'}`}
                data-testid="toggle-whatsapp"
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${settings.whatsapp_enabled ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
            <div>
              <label className="text-white/40 text-xs">Recipient Number</label>
              <input
                value={settings.whatsapp_recipient || ''}
                onChange={e => setSettings(s => ({ ...s, whatsapp_recipient: e.target.value }))}
                onBlur={() => save({ whatsapp_recipient: settings.whatsapp_recipient })}
                placeholder="+1234567890"
                className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none font-mono"
                data-testid="whatsapp-recipient-input"
              />
            </div>
          </div>
          <div className="bg-[#050505] border border-white/10 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#00D4FF]" />
                <span className="font-medium text-sm">Email Alerts (backup)</span>
              </div>
              <button
                onClick={() => save({ email_enabled: !settings.email_enabled })}
                className={`w-10 h-5 rounded-full transition-colors relative ${settings.email_enabled ? 'bg-[#00D4FF]' : 'bg-white/20'}`}
                data-testid="toggle-email"
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${settings.email_enabled ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
            <div>
              <label className="text-white/40 text-xs">Email Destination</label>
              <input
                value={settings.email_recipient || ''}
                onChange={e => setSettings(s => ({ ...s, email_recipient: e.target.value }))}
                onBlur={() => save({ email_recipient: settings.email_recipient })}
                placeholder="admin@luxortek.com"
                className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
                data-testid="email-recipient-input"
              />
            </div>
          </div>
        </div>

        {/* Individual alert toggles */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Alert Types</h3>
          <div className="space-y-2">
            {(settings.alert_types || []).map(type => {
              const info = ALERT_LABELS[type] || { label: type, icon: '-' };
              const enabled = settings.alerts?.[type] ?? true;
              return (
                <div key={type} className="flex items-center justify-between bg-[#050505] border border-white/10 px-4 py-3" data-testid={`alert-toggle-${type}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] text-xs font-bold">{info.icon}</span>
                    <span className="text-sm">{info.label}</span>
                  </div>
                  <button
                    onClick={() => toggleAlert(type)}
                    className={`w-10 h-5 rounded-full transition-colors relative ${enabled ? 'bg-[#00D4FF]' : 'bg-white/20'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${enabled ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dedup window */}
        <div className="mb-6 flex items-center gap-4">
          <label className="text-white/50 text-sm">Duplicate prevention window:</label>
          <select
            value={settings.dedup_window_minutes || 10}
            onChange={e => save({ dedup_window_minutes: parseInt(e.target.value) })}
            className="px-3 py-1.5 bg-black border border-white/10 text-white text-sm"
            data-testid="dedup-window-select"
          >
            <option value={5}>5 min</option>
            <option value={10}>10 min</option>
            <option value={30}>30 min</option>
            <option value={60}>1 hour</option>
          </select>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleTest}
            disabled={testing || !isConnected || !settings.whatsapp_recipient}
            className="btn-primary flex items-center gap-2 disabled:opacity-40"
            data-testid="test-alert-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {testing ? 'Sending...' : 'Send Test Alert'}
          </button>
          <button
            onClick={loadLogs}
            className="btn-secondary flex items-center gap-2 text-sm"
            data-testid="view-alert-log-btn"
          >
            <Clock className="w-4 h-4" />
            {showLog ? 'Hide Log' : 'View Alert Log'}
          </button>
          {saving && <span className="text-xs text-[#00D4FF] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving...</span>}
        </div>

        {/* Alert Log */}
        {showLog && (
          <div className="mt-4 bg-[#050505] border border-white/10 max-h-64 overflow-y-auto" data-testid="alert-log">
            {logLoading ? (
              <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-[#00D4FF]" /></div>
            ) : logs.length === 0 ? (
              <div className="p-4 text-center text-white/30 text-sm">No alerts logged yet</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-white/40">
                    <th className="p-2 text-left">Time</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-center">WhatsApp</th>
                    <th className="p-2 text-center">Email</th>
                    <th className="p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-2 text-white/50 font-mono">{new Date(log.created_at).toLocaleString()}</td>
                      <td className="p-2 text-white/70">{log.alert_type?.replace(/_/g, ' ')}</td>
                      <td className="p-2 text-center">
                        {log.whatsapp_success === true ? <CheckCircle className="w-3.5 h-3.5 text-[#10B981] mx-auto" /> :
                         log.whatsapp_success === false ? <XCircle className="w-3.5 h-3.5 text-[#EF4444] mx-auto" /> :
                         <span className="text-white/20">—</span>}
                      </td>
                      <td className="p-2 text-center">
                        {log.email_sent ? <CheckCircle className="w-3.5 h-3.5 text-[#10B981] mx-auto" /> :
                         <span className="text-white/20">—</span>}
                      </td>
                      <td className="p-2 text-white/40 truncate max-w-[200px]">
                        {log.skipped_reason ? <span className="text-[#F59E0B]">skipped: {log.skipped_reason}</span> :
                         log.whatsapp_error ? <span className="text-[#EF4444] truncate">{log.whatsapp_error}</span> :
                         log.whatsapp_success ? <span className="text-[#10B981]">delivered</span> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!isConnected && (
          <div className="mt-4 bg-[#F59E0B]/5 border border-[#F59E0B]/20 p-4 text-sm" data-testid="wa-setup-warning">
            <p className="text-[#F59E0B] font-medium mb-2">WhatsApp API Setup Required</p>
            <p className="text-white/50 mb-2">Add these environment variables to <code className="text-[#00D4FF] bg-black/50 px-1">/app/backend/.env</code>:</p>
            <div className="bg-black/50 p-3 font-mono text-xs space-y-1 text-white/60">
              <div>WA_PHONE_NUMBER_ID=<span className="text-[#F59E0B]">your_phone_number_id</span></div>
              <div>WA_ACCESS_TOKEN=<span className="text-[#F59E0B]">your_access_token</span></div>
              <div>WA_BUSINESS_ACCOUNT_ID=<span className="text-[#F59E0B]">your_waba_id</span></div>
              <div>WA_ALERT_RECIPIENT=<span className="text-[#F59E0B]">+1234567890</span></div>
            </div>
            <p className="text-white/40 text-xs mt-2">After adding credentials, restart the backend and click "Send Test Alert" to verify.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const CustomerPriceListExport = ({ t }) => {
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ category: '', brand: '', grade: '', in_stock_only: true });

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.brand) params.brand = filters.brand;
      if (filters.grade) params.grade = filters.grade;
      params.in_stock_only = filters.in_stock_only;

      const res = await exportCustomerPriceList(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `LuxorTek_Wholesale_Price_List_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Customer price list exported successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export price list');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mt-8" data-testid="customer-price-list-export">
      <div className="card-dark p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00D4FF]" />
              Sales Export Tools
            </h2>
            <p className="text-white/50 text-sm mt-1">
              Use this file for emailing wholesale customers a professional branded inventory price list.
            </p>
          </div>
        </div>

        <div className="bg-[#050505] border border-white/10 p-5 mb-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] shrink-0 mt-0.5">
              <Download className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-1">Customer Price List</h3>
              <p className="text-white/40 text-sm leading-relaxed">
                Generates a branded Excel workbook with separate tabs for each product type (NBK, PC, Workstation, AIO, Apple, LCD) plus a Grades &amp; Notes reference sheet. Includes company logo, formatted headers, and customer-ready pricing.
              </p>

              {/* Optional Filters */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="mt-3 flex items-center gap-1.5 text-xs text-[#00D4FF]/70 hover:text-[#00D4FF] transition-colors"
                data-testid="toggle-export-filters"
              >
                <Filter className="w-3.5 h-3.5" />
                {showFilters ? 'Hide filters' : 'Show optional filters'}
              </button>

              {showFilters && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3" data-testid="export-filters">
                  <div>
                    <label className="block text-white/40 text-xs mb-1">Category</label>
                    <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 bg-black border border-white/10 text-white text-sm" data-testid="export-filter-category">
                      <option value="">All</option>
                      <option value="NBK">NBK</option>
                      <option value="PC">PC</option>
                      <option value="WORKSTATION">Workstation</option>
                      <option value="AIO">AIO</option>
                      <option value="APPLE">Apple</option>
                      <option value="LCD">LCD</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs mb-1">Brand</label>
                    <input value={filters.brand} onChange={e => setFilters(f => ({ ...f, brand: e.target.value }))} placeholder="e.g. Dell" className="w-full px-3 py-2 bg-black border border-white/10 text-white text-sm" data-testid="export-filter-brand" />
                  </div>
                  <div>
                    <label className="block text-white/40 text-xs mb-1">Grade</label>
                    <select value={filters.grade} onChange={e => setFilters(f => ({ ...f, grade: e.target.value }))} className="w-full px-3 py-2 bg-black border border-white/10 text-white text-sm" data-testid="export-filter-grade">
                      <option value="">All</option>
                      <option value="A+">A+</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer pb-2">
                      <input type="checkbox" checked={filters.in_stock_only} onChange={e => setFilters(f => ({ ...f, in_stock_only: e.target.checked }))} className="accent-[#00D4FF]" data-testid="export-filter-stock" />
                      <span className="text-white/60 text-sm">In stock only</span>
                    </label>
                  </div>
                </div>
              )}

              <button
                onClick={handleExport}
                disabled={exporting}
                className="mt-4 btn-primary flex items-center gap-2"
                data-testid="export-customer-price-list-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exporting ? 'Generating...' : 'Export Customer Price List'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminChangePasswordSection = ({ t }) => {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error(t('changePassword.mismatch'));
      return;
    }
    if (newPw.length < 8) {
      toast.error(t('changePassword.tooShort'));
      return;
    }
    setSaving(true);
    try {
      await adminChangePassword({ current_password: currentPw, new_password: newPw });
      toast.success(t('changePassword.success'));
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 p-6 bg-[#0A0A0A] border border-white/10" data-testid="admin-change-password">
      <div className="flex items-center gap-3 mb-6">
        <KeyRound className="w-5 h-5 text-[#F59E0B]" />
        <h2 className="text-lg font-bold">{t('changePassword.title')}</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div>
          <label className="block text-sm text-white/60 mb-1">{t('changePassword.currentPassword')}</label>
          <input
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            required
            className="w-full px-4 py-2.5 bg-black border border-white/10 focus:border-[#00D4FF] text-white text-sm"
            data-testid="current-password-input"
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-1">{t('changePassword.newPassword')}</label>
          <input
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            required
            minLength={8}
            className="w-full px-4 py-2.5 bg-black border border-white/10 focus:border-[#00D4FF] text-white text-sm"
            data-testid="new-password-input"
          />
        </div>
        <div>
          <label className="block text-sm text-white/60 mb-1">{t('changePassword.confirmPassword')}</label>
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            minLength={8}
            className="w-full px-4 py-2.5 bg-black border border-white/10 focus:border-[#00D4FF] text-white text-sm"
            data-testid="confirm-password-input"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-[#F59E0B] text-black font-semibold hover:bg-[#F59E0B]/90 transition-colors disabled:opacity-50"
          data-testid="change-password-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          {saving ? t('changePassword.updating') : t('changePassword.submit')}
        </button>
      </form>
    </div>
  );
};
